import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import type { PropsWithChildren, ReactElement } from 'react';

/**
 * A workspace is the per-tenant container the dashboard scopes to. The
 * Pass-1 surface only needs id + name + slug; the editor pass adds richer
 * fields. The Apollo query that hydrates this state lands in Pass 2.
 */
export interface Workspace {
  id: string;
  name: string;
  slug: string;
  /**
   * True for the user's derived personal tenant (the `isPersonal` flag from
   * MyTenants). Optional because not every construction site carries it — the
   * onboarding "Skip — use Personal" path is the one consumer that needs it to
   * find the personal workspace without guessing at the slug.
   */
  isPersonal?: boolean;
  /**
   * The account (owner-level parent) this workspace belongs to (T10). Workspaces
   * sharing an `accountId` belong to the same owner — the grouping key for the
   * directory. Optional; null/absent for a legacy tenant not yet account-linked.
   */
  accountId?: string | null;
}

interface WorkspaceContextValue {
  workspaces: readonly Workspace[];
  currentWorkspaceId: string | null;
  currentWorkspace: Workspace | null;
  setWorkspaces: (next: readonly Workspace[]) => void;
  selectWorkspace: (id: string) => void;
  /**
   * Append a workspace and (by default) make it current. Avoids the
   * `setWorkspaces → selectWorkspace` race where the validating
   * `selectWorkspace` closure still sees the stale `workspaces` array.
   */
  addWorkspace: (workspace: Workspace, options?: { select?: boolean }) => void;
}

const STORAGE_KEY = 'syncanix:workspace';

/** Server-minted workspace ids are uuids; bootstrap seeds ('ws-personal') are not. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

function readStoredId(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(STORAGE_KEY);
}

export interface WorkspaceProviderProps extends PropsWithChildren {
  initialWorkspaces?: readonly Workspace[];
  initialWorkspaceId?: string | null;
}

export function WorkspaceProvider({
  children,
  initialWorkspaces = [],
  initialWorkspaceId = null,
}: WorkspaceProviderProps): ReactElement {
  const [workspaces, setWorkspacesState] = useState<readonly Workspace[]>(initialWorkspaces);
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string | null>(() => {
    if (initialWorkspaceId) return initialWorkspaceId;
    const stored = readStoredId();
    if (stored && initialWorkspaces.some((w) => w.id === stored)) return stored;
    // Before hydration the provider only has the non-uuid bootstrap seed
    // ('ws-personal'), so a persisted uuid selection can't be membership-
    // checked yet — trust it so the very first GraphQL requests carry the
    // right x-syncanix-tenant header. Otherwise the server silently falls
    // back to the personal tenant and first-load pages render the wrong
    // workspace's data (caught live 2026-06-10). Hydration's `setWorkspaces`
    // resets a stale/revoked id to the first real workspace afterwards.
    if (
      stored &&
      UUID_SHAPE.test(stored) &&
      !initialWorkspaces.some((w) => UUID_SHAPE.test(w.id))
    ) {
      return stored;
    }
    return initialWorkspaces[0]?.id ?? null;
  });

  const setWorkspaces = useCallback((next: readonly Workspace[]) => {
    setWorkspacesState(next);
    setCurrentWorkspaceId((prev) => {
      if (prev && next.some((w) => w.id === prev)) return prev;
      // The previously-selected workspace is gone (revoked, or a stale
      // persisted id that hydration couldn't confirm). Prefer the user's
      // personal workspace as the safe landing spot over an arbitrary
      // first-in-list tenant (A13).
      const fallback = (next.find((w) => w.isPersonal) ?? next[0])?.id ?? null;
      if (fallback && typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, fallback);
      } else if (!fallback && typeof window !== 'undefined') {
        window.localStorage.removeItem(STORAGE_KEY);
      }
      return fallback;
    });
  }, []);

  const addWorkspace = useCallback((workspace: Workspace, options?: { select?: boolean }) => {
    const select = options?.select ?? true;
    setWorkspacesState((prev) => {
      if (prev.some((w) => w.id === workspace.id)) return prev;
      return [...prev, workspace];
    });
    if (select) {
      setCurrentWorkspaceId(workspace.id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, workspace.id);
      }
    }
  }, []);

  const selectWorkspace = useCallback(
    (id: string) => {
      if (!workspaces.some((w) => w.id === id)) {
        throw new Error(`selectWorkspace: unknown workspace id "${id}"`);
      }
      setCurrentWorkspaceId(id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, id);
      }
    },
    [workspaces],
  );

  const currentWorkspace = useMemo<Workspace | null>(() => {
    if (!currentWorkspaceId) return null;
    return workspaces.find((w) => w.id === currentWorkspaceId) ?? null;
  }, [workspaces, currentWorkspaceId]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspaces,
      currentWorkspaceId,
      currentWorkspace,
      setWorkspaces,
      selectWorkspace,
      addWorkspace,
    }),
    [
      workspaces,
      currentWorkspaceId,
      currentWorkspace,
      setWorkspaces,
      selectWorkspace,
      addWorkspace,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- co-located hook by design
export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return ctx;
}
